import { Map } from 'immutable';
import shortid from 'shortid';
import { saveAs } from 'file-saver';
import { createAction, createApiAction } from '@saucelabs/sl-util-redux';
import { actions as notifications } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { push } from 'connected-react-router';

import * as api from './api';
import * as rdcManualTestingApi from './rdcManualTestingApi';
import * as types from './types';
import * as constants from './constants';
import * as utils from './utils';
import { trackEvent, MANUAL_EVENTS } from './tracking';
import { operations as lsmOperations } from './ducks/liveSessionManager';
import { actions as rdcSessionActions } from './ducks/rdcSession';
import {
  actions as testConfigurationActions,
  selectors as testConfigurationSelectors,
} from './ducks/testConfiguration';
import {
  actions as recentAndSavedConfigsActions,
  selectors as recentAndSavedConfigsSelectors,
} from './ducks/recentAndSavedConfigs';
import { operations as presetsOperations } from './ducks/presets';
// need to import directly from the file because of circular deps issue
import sessionCommonSelectors from './ducks/sessionCommon/selectors';

const MAX_CONFIGS_COUNT = 7;

const getOverallVMConcurrency = (concurrency) => {
  const allowed = concurrency.get('allowed', { overall: 0 }).overall;
  const current = concurrency.get('current', { overall: 0 }).overall;

  return allowed - current;
};

const hasOverallVMConcurrency = (concurrency) => !!getOverallVMConcurrency(concurrency);

export const cleanManualPath = (
  comingFromAppSession = false,
) => (comingFromAppSession ? push('/live/app-testing') : push('/live/web-testing'));

/* REQUESTS */
export const getMetadata = createApiAction(types.METADATA_GET, () => api.fetchMetadata());
export const putMetadata = createApiAction(types.METADATA_PUT, ({ page, key, data }) => (
  api.putMetadata(page, key, data)
));

export const showSauceConnectRdcPublicDisabled = ({
  children,
  header,
  dismissCb,
}) => (dispatch) => dispatch(notifications.notify({
  children,
  header,
  persist: true,
  dismissCb,
  category: notificationConstants.CATEGORY_WARNING,
  v2: true,
}));

// Mobile
export const fetchMobileDevice = createApiAction(
  types.MOBILE_DEVICE_FETCH,
  ({ deviceId }) => rdcManualTestingApi.fetchDevice({ deviceId }),
  { meta: { skipErrorPage: true } },
);

export const fetchMobileAppInfo = createApiAction(
  types.MOBILE_SESSION_APP_INFO_FETCH,
  ({ appId }, meta, dispatch) => api.fetchAppInfo(appId)
    .catch((error) => {
      dispatch(notifications.notifyDanger(error));
      throw error;
    }),
  { meta: { skipErrorPage: true } },
);

export const dismissMobileDeviceOverlay = createAction(types.MOBILE_DEVICE_ERROR_OVERLAY_DISMISS);

export const connectMobileDevice = createApiAction(
  types.MOBILE_DEVICE_CONNECT,
  ({ deviceSessionId, elementId, webRtcEnabled, webRtcCredentials }, meta, dispatch) => (
    rdcManualTestingApi.connectDevice({
      deviceSessionId,
      elementId,
      webRtcEnabled,
      webRtcCredentials,
    })
      .then((device) => {
        device.companion.setSessionWillExpireCallback(
          (secondsLeft) => dispatch(rdcSessionActions.secondsLeftUpdate(secondsLeft)),
        );
        device.companion.setSessionClosedCallback(
          () => dispatch(rdcSessionActions.setSessionClosedReason(
            constants.SESSION_INACTIVE_REASONS.EXPIRED),
          ),
        );
        return device;
      })
      .catch((error) => {
        dispatch(notifications.notifyDanger(error));
        throw error;
      })),
  { meta: { skipErrorPage: true } },
);

const handleRdcDeviceResponse = (data) => {
  if (data.status === constants.RDC_DEVICE_RESPONSE_STATUS.ERROR) {
    throw data.error;
  }

  return data;
};

export const getCreateMobileSessionError = (error) => {
  const {
    CREATE_MOBILE_SESSION_ERRORS,
    CREATE_MOBILE_SESSION_ERROR_MESSAGES,
    CREATE_MOBILE_SESSION_ERROR_MESSAGE_DEFAULT,
  } = constants;

  switch (error) {
    case CREATE_MOBILE_SESSION_ERRORS[error]:
      return CREATE_MOBILE_SESSION_ERROR_MESSAGES[error];
    default:
      return CREATE_MOBILE_SESSION_ERROR_MESSAGE_DEFAULT;
  }
};

/* MOBILE MANUAL SESSION */
export const createMobileSession = createApiAction(
  types.MOBILE_SESSION_CREATE,
  ({
    deviceId,
    elementId,
    appId,
    groupId,
    tunnel,
    webRtcEnabled,
  }, meta, dispatch, getState) => {
    const method = appId ?
      rdcManualTestingApi.createNativeAppSession : rdcManualTestingApi.createBrowserSession;
    const testReportName = sessionCommonSelectors.defaultTestNameSelector(getState());

    return method({
      deviceId,
      appId,
      groupId,
      testReportName,
      tunnel,
      webRtcEnabled,
    })
      .then((response) => {
        const deviceSessionId = (typeof response === 'string') ? response : response.deviceSessionId;
        const webRtcCredentials = (typeof response === 'string') ? null : response.webRtcCredentials;
        dispatch(connectMobileDevice({
          deviceSessionId,
          elementId,
          webRtcEnabled,
          webRtcCredentials,
        }));
        return response;
      })
      .catch((error) => {
        const message = getCreateMobileSessionError(error.response?.data || '');
        dispatch(notifications.notifyWarning(message.text, message.header, true, () => {}, true));
        throw error;
      });
  },
  { meta: { skipErrorPage: true } },
);

export const openUrlInMobileDevice = createApiAction(
  types.MOBILE_DEVICE_OPEN_URL,
  (url, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.openUrl(url).then(handleRdcDeviceResponse);
  },
  { meta: { skipErrorPage: true } },
);

export const pollAppInstallationStatus = (
  device,
  installationId,
  successCallback,
  failureCallback,
) => {
  const checkInstallationStatus = async () => {
    try {
      const response = await device.checkAppInstallationStatus(installationId);
      switch (response.status) {
        case constants.RDC_DEVICE_RESPONSE_STATUS.ERROR:
          throw new Error(response.error);
        case constants.RDC_DEVICE_RESPONSE_STATUS.FINISHED:
          successCallback();
          break;
        default:
          // The endpoint delays the response up to 15 seconds,
          // setTimeout is only used here to protect the UI if
          // that delay doesn't work for some reason
          setTimeout(checkInstallationStatus, 100);
          break;
      }
    } catch (error) {
      failureCallback(error);
    }
  };

  checkInstallationStatus();
};

const installMobileAppRequest = createAction(types.MOBILE_DEVICE_APP_INSTALL.REQUEST);
const installMobileAppSuccess = createAction(types.MOBILE_DEVICE_APP_INSTALL.SUCCESS);
const installMobileAppFailure = createAction(types.MOBILE_DEVICE_APP_INSTALL.FAILURE);

export const installMobileApp = ({
  appId, groupId, disconnectIo,
}) => async (dispatch, getState) => {
  dispatch(installMobileAppRequest());

  const { apps: { manual: { session } } } = getState();
  const device = session.get('device');
  const shouldLaunchApp = true;
  if (disconnectIo && device.io?.disconnect) {
    device.io.disconnect();
  }

  try {
    const response = await device.installAppFromAppStorage(
      appId,
      groupId,
      shouldLaunchApp,
    );

    switch (response.status) {
      case constants.RDC_DEVICE_RESPONSE_STATUS.ERROR:
        throw new Error(response.error);
      case constants.RDC_DEVICE_RESPONSE_STATUS.FINISHED:
        dispatch(installMobileAppSuccess());
        break;
      default:
        // Start polling for the installation status
        pollAppInstallationStatus(
          device,
          response.id,
          // success callback
          () => {
            dispatch(installMobileAppSuccess());
          },
          // failure callback
          (error) => {
            dispatch(installMobileAppFailure(error.message));
            dispatch(notifications.notifyDanger(error.message));
          },
        );
        break;
    }
  } catch (error) {
    dispatch(installMobileAppFailure(error.message));
    dispatch(notifications.notifyDanger(error.message));
  }
};

export const pasteContentMobileDevice = createApiAction(
  types.MOBILE_DEVICE_PASTE_CONTENT,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    return Promise.all([
      device.pasteText(payload),
      dispatch(notifications.notifySuccess('Pasting content...')),
    ]);
  },
  { meta: { skipErrorPage: true } },
);

export const executeADBShell = createApiAction(
  types.MOBILE_DEVICE_EXECUTE_ADB_SHELL,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return Promise.all([
      device.executeShellCommand(payload),
      dispatch(notifications.notifySuccess('Executing ADB shell command...')),
      // eslint-disable-next-line no-console
    ]).then(([message]) => console.log(`ADB shell output: "${message.text()}"`));
  },
  { meta: { skipErrorPage: true } },
);

export const restartMobileDeviceApp = createApiAction(
  types.MOBILE_DEVICE_APP_RESTART,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return Promise.all([
      device.relaunchCurrentApp(),
      dispatch(notifications.notifySuccess('Restarting your app...')),
    ]);
  },
  { meta: { skipErrorPage: true } },
);

export const toggleMobileDeviceAnimations = createApiAction(
  types.MOBILE_DEVICE_ANIMATIONS_TOGGLE,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    const animationsEnabled = getState().apps.manual.session.get('deviceAnimationsEnabled');

    const methodName = animationsEnabled ? 'disableAnimations' : 'enableAnimations';

    return device[methodName]().then(() => (
      dispatch(notifications.notifySuccess(`Animations successfully ${animationsEnabled ? 'disabled' : 'enabled'}.`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const toggleMobileDeviceWifi = createApiAction(
  types.MOBILE_DEVICE_WIFI_TOGGLE,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    const wifiEnabled = getState().apps.manual.session.get('deviceWifiEnabled');

    const methodName = wifiEnabled ? 'disableWifi' : 'enableWifi';

    return device[methodName]().then(() => (
      dispatch(notifications.notifySuccess(`WiFi successfully ${wifiEnabled ? 'disabled' : 'enabled'}.`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const fetchMobileDeviceLanguages = createApiAction(
  types.MOBILE_DEVICE_LANGUAGES_FETCH,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.getLocales();
  },
  { meta: { skipErrorPage: true } },
);

export const setMobileDeviceLanguage = createApiAction(
  types.MOBILE_DEVICE_LANGUAGE_SET,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.setLocale(payload.id).then((updatedLocale) => (
      dispatch(notifications.notifySuccess(`Language successfully set to ${updatedLocale.label}`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const rotateMobileDeviceRequest = createAction(types.MOBILE_DEVICE_ROTATE.REQUEST);
export const rotateMobileDeviceSuccess = createAction(types.MOBILE_DEVICE_ROTATE.SUCCESS);
export const rotateMobileDevice = () => (dispatch, getState) => {
  const device = getState().apps.manual.session.get('device');

  return new Promise((resolve) => {
    device.onRotationStart((orientation) => dispatch(rotateMobileDeviceRequest(orientation)));
    device.onRotationFinish((orientation) => {
      dispatch(rotateMobileDeviceSuccess(orientation));
      resolve();
    });

    device.rotate();
  });
};

export const shareMobileDeviceSession = createApiAction(
  types.MOBILE_DEVICE_SHARE_SESSION,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    return device.share();
  },
  { meta: { skipErrorPage: true } },
);

export const pressMobileDeviceHardwareButton = createApiAction(
  types.MOBILE_DEVICE_HARDWARE_BUTTON_PRESS,
  (hardwareButtonType, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    switch (hardwareButtonType) {
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.BACK:
        return Promise.resolve(device.pressBack());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.HOME:
        return Promise.resolve(device.pressHome());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.MENU:
        return Promise.resolve(device.pressMenu());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.SEARCH:
        return Promise.resolve(device.pressSearch());
      default:
        throw new Error(`Unknown hardware button type '${hardwareButtonType}'`);
    }
  },
  { meta: { skipErrorPage: true } },
);

export const toggleWebRtcAudio = createAction(types.TOGGLE_WEBRTC__AUDIO);

export const setPerformanceMode = createAction(types.MOBILE_DEVICES_PERFORMANCE_MODE_SET);

export const updateMobileDeviceState = createAction(types.MOBILE_DEVICE_STATE_UPDATE);

export const takeMobileDeviceScreenshot = createApiAction(
  types.MOBILE_DEVICE_TAKE_SCREENSHOT,
  (payload, meta, dispatch, getState) => {
    dispatch(trackEvent(MANUAL_EVENTS.TAKE_RDC_SCREENSHOT));
    const state = getState();
    const device = state.apps.manual.session.get('device');
    const deviceDescriptor = state.apps.manual.session.get('deviceDescriptor');
    const userName = state.user.get('username');
    const appName = state.router.location.state.appName || '';
    // filename format:
    // <timestamp>_<username>_<appname(app testing only)>_<deviceId>_<deviceVersion>
    const filename = `${new Date().toISOString()}_` +
      `${userName}_` +
      `${appName ? `${appName}_` : ''}` +
      `${deviceDescriptor.compositeId}_` +
      `${deviceDescriptor.osVersion}.png`;

    return rdcManualTestingApi.takeScreenshot(device.deviceSessionDescriptor.deviceSessionId)
      .then((response) => {
        saveAs(new Blob([response.data]), filename);
      })
      .catch((error) => {
        dispatch(notifications.notifyDanger(error));
        throw error;
      });
  },
  { meta: { skipErrorPage: true } },
);

export const stopMobileSession = createApiAction(
  types.MOBILE_SESSION_STOP,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return new Promise((resolve) => {
      if (device) {
        device.close();

        window.removeEventListener('resize', device.resize);
        window.removeEventListener('beforeunload', device.close);
      }
      resolve();
    });
  },
  { meta: { skipErrorPage: true } },
);

export const handleCreateSession = (payload, dispatch) => {
  const { deviceType, realDeviceConfiguration } = payload;
  const {
    CONFIGURATION_TEST_TYPES: { APP, WEB },
    CONFIGURATION_DEVICE_TYPES: { DESKTOP_BROWSER, REAL_DEVICE, VIRTUAL_DEVICE },
  } = constants;

  if (deviceType === VIRTUAL_DEVICE || deviceType === DESKTOP_BROWSER) {
    return dispatch(lsmOperations.startSession(payload));
  }

  if (deviceType === REAL_DEVICE && realDeviceConfiguration) {
    const { mobileAppConfiguration, testType, tunnel } = payload;

    return dispatch(
      push(
        `/live/mobile/dataCenters/${realDeviceConfiguration.dataCenterId}/devices/${realDeviceConfiguration.descriptorId}`,
        {
          tunnel,
          ...((testType === WEB) && { url: payload.url }),
          ...((testType === APP) && {
            appId: mobileAppConfiguration.id,
            appName: mobileAppConfiguration.metadata?.name || mobileAppConfiguration.name,
            groupId: mobileAppConfiguration.group_id,
          }),
        },
      ),
    );
  }

  return Promise.resolve();
};

export const handleTabsFormSubmit = () => async (dispatch, getState) => {
  dispatch(testConfigurationActions.updateTestConfigValue({ isSessionLoading: true }));

  const state = getState();
  const payload = testConfigurationSelectors.handleTabsFormSubmitSelector(state);
  const { deviceType } = payload;
  const isRdcTest = deviceType === constants.CONFIGURATION_DEVICE_TYPES.REAL_DEVICE;

  const { apps: { manual: { realDeviceSelection } } } = state;

  const concurrency = concurrencySelectors.concurrencySelector(getState());
  const overallVMConcurrency = hasOverallVMConcurrency(concurrency.user);
  const rdcSessionAvailable = realDeviceSelection.get('sessionAvailable');

  const isPrivateDevice = payload.realDeviceConfiguration?.isPrivateDevice() || false;

  const concurrencyForDeviceTypeAvailable = (
    (isRdcTest && isPrivateDevice) || // Private devices don't need available concurrency
    (isRdcTest && rdcSessionAvailable) ||
    (!isRdcTest && overallVMConcurrency)
  );

  const fixedPayload = {
    ...payload,
    url: payload.url ? payload.url.trim() : constants.SESSION_DEFAULT_URL,
  };

  const recentConfigs = recentAndSavedConfigsSelectors.recentConfigsSelector(state);
  const savedConfigs = recentAndSavedConfigsSelectors.savedConfigsSelector(state);
  const configObject = utils.getLiveTestingConfig(fixedPayload);
  const newConfig = configObject && Map({
    ...configObject,
    id: shortid.generate(),
  });

  if (concurrencyForDeviceTypeAvailable) {
    handleCreateSession(fixedPayload, dispatch, getState);

    const dispatchPromises = [];

    dispatchPromises.push(dispatch(presetsOperations.prepareAndStoreRecentPreset(fixedPayload)));

    if (newConfig) {
      dispatchPromises.push(dispatch(recentAndSavedConfigsActions.putRecentConfigs(
        recentConfigs
          .insert(0, newConfig)
          .slice(0, MAX_CONFIGS_COUNT),
      )));
    }

    if (fixedPayload.saveConfiguration && newConfig) {
      dispatchPromises.push(dispatch(recentAndSavedConfigsActions.putSavedConfigs(
        savedConfigs
          .insert(0, newConfig)
          .slice(0, MAX_CONFIGS_COUNT),
      )));
    }

    return Promise.all(dispatchPromises);
  }

  return Promise.resolve();
};
